// Content-addressed object keys, SHARED by photo ingest (#323) and the
// multi-video footage upload behind `multiVideoV1`.
//
// A key becomes the storage object path's last segment and, via the server
// storage trigger, the Firestore doc id — so the SAME physical file must
// produce the SAME key on every upload, or dedup fails and we double-store.
//
// Shape: `${stableSeconds}-${size}-${sanitizedName}`
//   - stableSeconds is a STABLE timestamp in WHOLE seconds, supplied by the
//     caller (photos: EXIF capture time, or 0 when absent; videos: the file's
//     lastModified). Whole seconds on purpose — browsers re-stamp
//     `File.lastModified` with ~1 ms of jitter on each folder drop, which gave
//     the same file a different key on re-upload when milliseconds were used.
//   - sanitized name keeps [A-Za-z0-9._-], replaces everything else with _
//   - collapses adjacent underscores from the substitution
//   - trims leading dots (Firestore doc IDs may not start with .)
//   - final key is truncated so the whole string is <= maxLength
//
// The truncation preserves the leading `stableSeconds-size-` prefix and cuts
// off the tail of the sanitized name. Two files that differ only in a truncated
// suffix could collide — but for an ingest batch that's the same file logically
// and the collision is the intended dedup behavior.

export const MAX_OBJECT_KEY_LENGTH = 200;
const ALLOWED_CHAR_REGEX = /[^A-Za-z0-9._-]/g;
const MULTI_UNDERSCORE_REGEX = /_+/g;
const LEADING_DOT_REGEX = /^\.+/;

export function sanitizeFileName(name, { fallback = 'file' } = {}) {
  if (typeof name !== 'string' || !name) return fallback;
  const cleaned = name
    .replace(ALLOWED_CHAR_REGEX, '_')
    .replace(MULTI_UNDERSCORE_REGEX, '_')
    .replace(LEADING_DOT_REGEX, '');
  return cleaned || fallback;
}

export function buildObjectKey(file, {
  stableAtMs,
  maxLength = MAX_OBJECT_KEY_LENGTH,
  fallbackName = 'file',
  discriminator = null,
} = {}) {
  if (!file) throw new Error('buildObjectKey: file is required');
  // Whole seconds — guards against any sub-second variance and keeps the key
  // short. 0 when there's no stable time (falls back to size+name).
  const seconds = Number.isFinite(stableAtMs) ? Math.floor(stableAtMs / 1000) : 0;
  const size = Number.isFinite(file.size) ? file.size : 0;
  const sanitized = sanitizeFileName(file.name, { fallback: fallbackName });
  // An optional extra identity component (videos pass a hash of sampled file
  // CONTENT). It goes in the PREFIX, not the tail, because the tail is what
  // gets truncated for a long name — appended, a 200-char filename would cut
  // the discriminator off and reintroduce the collision it exists to prevent.
  const prefix = discriminator
    ? `${seconds}-${size}-${discriminator}-`
    : `${seconds}-${size}-`;
  const maxNameLength = Math.max(1, maxLength - prefix.length);
  const trimmedName = sanitized.length > maxNameLength
    ? sanitized.slice(0, maxNameLength)
    : sanitized;
  return `${prefix}${trimmedName}`;
}
